<template>
  <portlet
      v-if="getConfig"
          :actionsBlock="true"
          :icon="'fad fa-boxes'"
          :title="$t('productivity.title')"
  >
    <template slot="portletBody">
      <div>
        <button class="m-show-lg btn btn-secondary w-100 mb-4" @click="filterOpen = !filterOpen">
          <i class="far mr-3" :class="{'fa-filter': !filterOpen, 'fa-times': filterOpen }"/>
          <template v-if="!filterOpen">
            {{$t('filter.workflows')}}
          </template>
          <template v-else>
            {{$t('filter.close')}}
          </template>
        </button>
        <div class="row dataTable-filters orderlines-dt-filter mb-4 pb-3" :class="filterOpenClass">
          <div class="col-md-4 col-xl">
            <div class="dropdown">
              <button class="btn btn-outline-filter dropdown-toggle" type="button">
                <span>{{$t('productivity.workflow')}} ({{customFilters[0].options.values.length}})</span>
              </button>
              <div class="dropdown-menu">
                <input v-model="searchWorkflow" class="form-control mb-3" type="text"
                       :placeholder="$t('productivity.filter.search')">
                <div class="option-wrapper">
                  <div class="custom-control custom-checkbox" v-for="(workflow, index) in searchByWorkflow"
                       :key="workflow+'-'+index">
                    <input v-model="customFilters[0].options.values"
                           :value="workflow.id"
                           type="checkbox"
                           :id="'workflow-'+index"
                           class="custom-control-input"
                    >
                    <label class="custom-control-label" :for="'workflow-'+index">
                      <span>{{workflow.name}}</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <data-tables
                :columns="getColumns()"
                :rows="getProductionRunLogs"
                :tableClass="tableClass"
                :tableInitOptions="tableInitOptions"
                @rowSelected="openExplanationNote($event)"
                class="first-checkbox"
                deleteClass="delete"
                routerLinkClass="dt-router-link"
                :customFilters="customFilters"
                tableID="productivity-dt"
                v-if="getProductionRunLogs"
        />
      </div>
    </template>

    <div class="col text-right" slot="portletFooter">
      <button class="btn btn-cancel">{{$t('ui.cancel')}}</button>
      <button class="btn btn-primary ml-4" type="submit">{{$t('ui.save')}}</button>
    </div>
  </portlet>
</template>

<script>
  import {mapGetters} from 'vuex';
  import {dataTables} from '../../mixins/dataTables';

  // @vuese
  // @group Productivity
  // Productivity logs that are generated from the start-stop page.
  export default {
    name: 'ProductivityLogs',
    mixins: [dataTables],
    data() {
      return {
        searchWorkflow: '',
        productionLog: [],
        filterOpen: false,
        filterOpenClass: 'm-hide-lg',
        tableInitOptions: {
          order: [[4, 'desc']]
        },
        customFilters: [
          {
            filter: 'arrayContains',
            keyToFilter: 'workflowId',
            options: {
              values: []
            }
          }
        ]
      };
    },
    computed: {
      ...mapGetters('productionRuns', [
        'getProductionRuns',
        'getProductionRunLogs'
      ]),
      ...mapGetters('company', [
        'getConfig'
      ]),
      ...mapGetters('orders', [
        'getOrderTarget'
      ]),
      ...mapGetters('orderlines', [
        'getOrderLineTarget'
      ]),
      ...mapGetters('jobs', [
        'getJobTarget'
      ]),
      ...mapGetters('workflows', [
        'getWorkflows'
      ]),
      ...mapGetters('users', ['getMe', 'getSelectedWorkflowLength', 'getSelectedWorkflow']),
      // @vuese
      // Search by workflow
      searchByWorkflow() {
        return this.getWorkflows.filter((search) => {
          return search.id.indexOf(this.searchWorkflow.toLowerCase()) >= 0;
        });
      }
    },
    methods: {
      /**
       * @vuese
       * Checks if an explanation is needed.
       * An explanation is only needed if the target hasn't been reached && when there's no explanation yet.
       * returns `Boolean`
       * @arg `explanation` the producionRun explanation object.
       * @arg `amountProduced` the amount of units produced during the run
       * @arg `startTimestamp` the date object of the time that the run started
       * @arg `endTimestamp` the date object of the time that the run ended
       * @arg `targetPerMs` the amount of milliseconds it takes to produce one unit
       *
       * @returns {Boolean}
       */
      explanationNoteNeeded(explanation, amountProduced, startTimestamp, endTimestamp, targetPerMs) {
        let passed = this.getProductionRunResult(amountProduced, startTimestamp, endTimestamp, targetPerMs);
        if(passed) {
          return false;
        } else {
          return !explanation.note.length;
        }
      },
      /**
       * @vuese
       * This finds out if the if the number of units or timespent it produced within the desired targets.
       *
       * @arg `amountProduced` the amount of units produced during the run
       * @arg `startTimestamp` the date object of the time that the run started
       * @arg `endTimestamp` the date object of the time that the run ended
       * @arg `targetPerMs` the amount of milliseconds it takes to produce one unit
       *
       * @returns {Boolean}
       */
      getProductionRunResult(amountProduced, startTimestamp, endTimestamp, targetPerMs) {
        if(this.getConfig.startStop.productivity !=='amount'){
          return true
        }
        let targetForThisDuration = this.gettargetForDuration(startTimestamp, endTimestamp, targetPerMs);
        return targetForThisDuration <= amountProduced;
      },
      /**
       * @vuese
       * Calculates the number of units that have to been produced given a start and an endtime.
       * We floor the target, because if the target is 10.1 per hour, you can make 10 per hour.
       * But to reach 10.1 you'd have to produce 11 units, which is not possible because 10.1 is the limit
       * of the machine.
       *
       * @arg `startTimestamp` the date object of the time that the run started
       * @arg `endTimestamp` the date object of the time that the run ended
       * @arg `targetPerMs` the amount of milliseconds it takes to produce one unit
       *
       * @returns {Number}
       */
      gettargetForDuration(startTimestamp, endTimestamp, targetPerMs) {
        return Math.floor((endTimestamp.getTime() - startTimestamp.getTime()) / targetPerMs);
      },
      /**
       * @vuese
       * Opens the modal with the full explanation for not reaching the target.
       * If there is already an explanation filled in: it opens a read-only modal.
       * If there is not explanation yet: it opens a modal with textarea.
       *
       * @arg `productionRun` the entire productionRun (entire row that has been clicked)
       *
       * @returns {Void}
       */
      openExplanationNote(productionRun) {
        var customClass = {
          content: 'explanationNoteContent',
          header: 'explanationNoteHeader'
        };
        if(productionRun.explanation.note.length) {
          this.$swal({
            title: this.$t('productivity.explanationNote'),
            text: productionRun.explanation.note,
            reverseButtons: true,
            customClass: customClass
          });
        } else {
          this.$swal({
            title: this.$t('productivity.explanationNote'),
            input: 'textarea',
            confirmButtonText: this.$t('ui.save'),
            showCancelButton: true,
            reverseButtons: true,
            inputPlaceholder: this.$t('productivity.placeholderExplanationNote'),
            customClass: customClass
          }).then((result) => {
            if(result.value.length) {
              this.saveExplanationNote(productionRun.id, result.value);
            }
          });
        }
      },
      /**
       * @vuese
       * Saves the entered explanation in the firestore and shows a succes/failed toast
       *
       * @arg `productionRunId` the ID of the production run
       * @arg `note` the entered explanation in the textarea
       *
       * @returns {Void}
       */
      saveExplanationNote(productionRunId, note) {
        this.$store.dispatch('productionRuns/set', {
          id: productionRunId,
          explanation: {userId: this.getMe.id, note: note}
        }).then(() => {
              this.$toasted.global.successRequest({
                message: this.$t('productivity.explanationSavedSuccess')
              });
            },
            () => {
              this.$toasted.global.failedRequest({
                message: this.$t('productivity.explanationSavedFailed')
              });
            });
      },
      /**
       * @vuese
       * Gets the columns for the table and formats the data
       * Sets up the columns for datatables package from https://git.motivate.nl/js-awesome/motivate-framework-components/blob/v2/src/components/DataTable.vue
       *
       * @returns {Array}
       */

      getColumns() {
        let self = this;
        return [
          {
            data: null,
            name: this.$t('productivity.result'),
            searchable: false,
            className: 'alignCenter',
            sortable: false,
            visible: self.getConfig.startStop.productivity == 'amount',
            render(data) {
              let passed = self.getProductionRunResult(data.amountProduced, data.productionStart, data.productionEnd, data.target);
              let explanationNoteNeeded = self.explanationNoteNeeded(data.explanation, data.amountProduced, data.productionStart, data.productionEnd, data.target);
              return `<span class="runDot ${passed ? 'runSuccess' : 'runFailed'} ${explanationNoteNeeded ? 'bouncingAnimation' : ''}" ${explanationNoteNeeded ? 'title="' + self.$t('productivity.explanationNeeded') + '"' : ''}></span>`;
            }
          },
          {
            data: null,
            name: this.$t('productivity.product'),
            searchable: false,
            sortable: false,
            visible: false,
            render(data) {
              let explanationNoteNeeded = self.explanationNoteNeeded(data.explanation, data.amountProduced, data.productionStart, data.productionEnd, data.target);
              return `<div ${explanationNoteNeeded ? 'class="text-danger text-bold"' : ''} >${data.product}</div>`;
            }
          },
          {
            data: null,
            name: this.$t('productivity.order'),
            searchable: true,
            sortable: false,
            visible: true,
            render(data) {
              let explanationNoteNeeded = self.explanationNoteNeeded(data.explanation, data.amountProduced, data.productionStart, data.productionEnd, data.target);
              return `<div ${explanationNoteNeeded ? 'class="text-danger text-bold"' : ''} >${data.orderNumber}</div>`;
            }
          },
          {
            data: null,
            name: this.$t('productivity.workflow'),
            searchable: false,
            sortable: true,
            render(data) {
              let explanationNoteNeeded = self.explanationNoteNeeded(data.explanation, data.amountProduced, data.productionStart, data.productionEnd, data.target);
              return `<div ${explanationNoteNeeded ? 'class="text-danger text-bold"' : ''} >${data.workflow ? data.workflow.name : ''}</div>`;
            }
          },
          {
            data: null,
            name: this.$t('productivity.startTime'),
            searchable: false,
            sortable: true,
            render(data) {
              let explanationNoteNeeded = self.explanationNoteNeeded(data.explanation, data.amountProduced, data.productionStart, data.productionEnd, data.target);
              return `<div style="display: none">${moment(data.productionStart).format('YYYY-MM-DD HH:mm')}</div><div ${explanationNoteNeeded ? 'class="text-danger text-bold"' : ''} >${moment(data.productionStart).format('DD-MM-YYYY HH:mm')}</div>`;
            }
          },
          {
            data: null,
            name: this.$t('productivity.stopTime'),
            searchable: false,
            sortable: true,
            render(data) {
              let explanationNoteNeeded = self.explanationNoteNeeded(data.explanation, data.amountProduced, data.productionStart, data.productionEnd, data.target);
              return `<div style="display: none">${moment(data.productionEnd).format('YYYY-MM-DD HH:mm')}</div><div ${explanationNoteNeeded ? 'class="text-danger text-bold"' : ''} >${moment(data.productionEnd).format('DD-MM-YYYY HH:mm')}</div>`;
            }
          },
          {
            data: null,
            name: this.$t('productivity.duration'),
            searchable: false,
            sortable: true,
            render(data) {
              let explanationNoteNeeded = self.explanationNoteNeeded(data.explanation, data.amountProduced, data.productionStart, data.productionEnd, data.target);
              let momentDuration = moment.duration((data.productionEnd - data.productionStart));
              let duration = moment.duration(momentDuration);
              let formatted = moment.utc(duration.asMilliseconds()).format('HH:mm:ss');
              return `<div ${explanationNoteNeeded ? 'class="text-danger text-bold"' : ''} >${formatted}</div>`;
            }
          },
          {
            data: null,
            name: this.$t('productivity.unitsProduced'),
            searchable: false,
            sortable: true,
            visible: self.getConfig.startStop.productivity == 'amount',
            render(data) {

              let explanationNoteNeeded = self.explanationNoteNeeded(data.explanation, data.amountProduced, data.productionStart, data.productionEnd, data.target);
              return `<div ${explanationNoteNeeded ? 'class="text-danger text-bold"' : ''} >${data.amountProduced ? data.amountProduced : 0}</div>`;
            }
          },
          {
            data: null,
            name: this.$t('productivity.target'),
            searchable: false,
            sortable: true,
            render(data) {
              let target =  self.getJobTarget(data.jobId) ;
              let explanationNoteNeeded = self.explanationNoteNeeded(data.explanation, data.amountProduced, data.productionStart, data.productionEnd, data.target);

              target = moment.unix(target/1000).format('mm:ss');
              return `<div ${explanationNoteNeeded ? 'class="text-danger text-bold"' : ''} >${target}</div>`;
            }
          },
          {
            data: null,
            name: this.$t('productivity.explanationNote'),
            searchable: false,
            width: '20em',
            sortable: false,
            visible: self.getConfig.startStop.productivity == 'amount',
            render(data) {
              let passed = self.getProductionRunResult(data.amountProduced, data.productionStart, data.productionEnd, data.target);
              if(passed) {
                return null;
              } else {
                if(data.explanation.note.length) {
                  // The explanation note is added, so now we change the functionality from 'add note' to 'view note', 
                  // while the button gets replaced by the first n characters of the note. 
                  // But to open the modal (to view the entire note) we added a hidden button that gets fired when the note
                  // gets clicked.
                  return `<button id="hiddenExplanationButton_${data.id}" hidden class="btn btn-primary get-data"></button>\
                  <i onclick="$('#hiddenExplanationButton_${data.id}').click()" class="fad fa-external-link explanationNoteShortIcon"></i><span class="explanationNoteShort" onclick="$('#hiddenExplanationButton_${data.id}').click()" title="${data.explanation.note}">${data.explanation.note}</span>`;
                } else {
                  return `<button class="btn btn-danger get-data"><i class="fad fa-edit"></i> ${self.$t('productivity.addExplanationNote')}</button>`;
                }
              }

            }
          }
        ];
      }
    },
    watch: {
      // @vuese
      // checks if filter is open
      filterOpen: function (filter) {
        if(filter) {
          this.filterOpenClass = 'm-show-flex-lg';
        } else {
          this.filterOpenClass = 'm-hide-lg';
        }
      }
    },
    created() {
      // Sets the current selected workflow as default turned on filter for the table.
      if(this.getSelectedWorkflowLength) {
        this.customFilters[0].options.values.push(this.getSelectedWorkflow);
      }
    }
  };
</script>